import { useCallback, useState } from 'react';
import { CrownSimple, EnvelopeSimple, Phone, Trash } from '@phosphor-icons/react';
import { BestaetigenDialog, InnenKarte, KartenTitel, SearchSelect, ToastPill, useToastPill, type SucheOption } from '../../../../../components/immobilien/ui';
import { eur, initialen } from '../../../../../lib/immobilien/format';
import { useOpenKontakt } from '../../../../../lib/immobilien/openKontakt';
import { eigentuemerAnzeigename } from '../../../../../lib/eigentuemer/anzeige';
import { setzeObjektEigentuemerListe } from '../../../../../lib/eigentuemer/verknuepfung';
import { supabase } from '../../../../../lib/supabase/client';
import { bereinigeSuchbegriff } from '../../../../../lib/kontakte/personenSuche';
import { meldeObjektGeaendert } from '../../../../../lib/immobilien/useObjektKontext';
import type { EigentuemerZeile } from '../../../../../lib/immobilien/useObjektDetail';
import { useObjektSeite } from '../ObjektSeitenKontext';

/** Suche in allen Eigentümern (≈ 500) – höchstens 8 Treffer. */
export async function sucheEigentuemer(anfrage: string, _typ: string | null, signal: AbortSignal): Promise<SucheOption[]> {
  const q = bereinigeSuchbegriff(anfrage).replace(/%/g, ' ');
  const muster = `%${q}%`;
  const { data, error } = await supabase.from('eigentuemer')
    .select('id, anrede, vorname, nachname, firma_name, email, stadt')
    .or(`vorname.ilike.${muster},nachname.ilike.${muster},firma_name.ilike.${muster},email.ilike.${muster}`)
    .order('nachname').limit(8).abortSignal(signal);
  if (error) throw new Error(error.message);
  return (data ?? []).map(e => ({ id: e.id as string, label: eigentuemerAnzeigename(e), sub: [e.email, e.stadt].filter(Boolean).join(' · ') || undefined, typ: 'eigentuemer' }));
}

/** Panel „Eigentümer“: Eigentümer des Objekts (eine Quelle: objekt_eigentuemer) und bei WEG die Einheiten-Eigentümer. */
export function EigentuemerPanel() {
  const { daten, neuLaden, verwaltungsart, uebersicht } = useObjektSeite();
  const openKontakt = useOpenKontakt();
  const [toast, zeige] = useToastPill();
  const [entfernen, setEntfernen] = useState<EigentuemerZeile | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const objektId = daten.objekt.id;

  const setzeListe = useCallback(async (ids: string[], meldung: string) => {
    setLaeuft(true);
    try {
      await setzeObjektEigentuemerListe(objektId, ids);
      zeige(meldung);
      neuLaden('eigentuemer', 'objekt');
      meldeObjektGeaendert(objektId);
    } catch (e) {
      zeige(`Nicht gespeichert: ${e instanceof Error ? e.message : 'Unbekannt'}`, true);
    } finally {
      setLaeuft(false);
    }
  }, [objektId, neuLaden, zeige]);

  const ids = daten.eigentuemer.map(e => e.id);
  const hinzufuegen = (o: SucheOption) => {
    if (ids.includes(o.id)) { zeige(`${o.label} ist bereits Eigentümer`, true); return; }
    void setzeListe([...ids, o.id], `${o.label} als Eigentümer verknüpft`);
  };

  const bez = new Map(uebersicht.map(e => [e.id, e.bezeichnung]));
  const wegZeilen = [...daten.weg].sort((a, b) => (bez.get(a.einheit_id) ?? '').localeCompare(bez.get(b.einheit_id) ?? '', 'de', { numeric: true }));

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <InnenKarte>
        <KartenTitel icon={CrownSimple} rechts={`${daten.eigentuemer.length} verknüpft`}>Eigentümer des Objekts</KartenTitel>
        {daten.eigentuemer.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Noch kein Eigentümer verknüpft.</span>}
        {daten.eigentuemer.map(e => {
          const name = eigentuemerAnzeigename(e);
          return (
            <div key={e.id} className="flex items-center flex-wrap" style={{ gap: 12, padding: 10, borderRadius: 16, background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
              <span className="flex items-center justify-center shrink-0" style={{ width: 42, height: 42, borderRadius: '50%', background: 'var(--knob)', fontSize: 14, fontWeight: 700 }}>{initialen(name)}</span>
              <button type="button" onClick={() => openKontakt(e.id, 'eigentuemer')} className="flex flex-col flex-1 min-w-0 text-left cursor-pointer" style={{ border: 'none', background: 'transparent', padding: 0, color: 'var(--ink)', minWidth: 160 }}>
                <span className="truncate" style={{ fontSize: 14, fontWeight: 800 }}>{name}</span>
                <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{[e.email, e.telefon].filter(Boolean).join(' · ') || 'Keine Kontaktdaten'}</span>
              </button>
              <div className="flex" style={{ gap: 6 }}>
                {e.telefon && <a href={`tel:${e.telefon.replace(/\s+/g, '')}`} aria-label={`${name} anrufen`} className="flex items-center justify-center" style={{ width: 36, height: 36, borderRadius: 12, background: 'var(--ok-soft)', color: 'var(--ok)' }}><Phone weight="fill" size={16} /></a>}
                {e.email && <a href={`mailto:${e.email}`} aria-label={`E-Mail an ${name}`} className="flex items-center justify-center" style={{ width: 36, height: 36, borderRadius: 12, background: 'var(--acc-soft)', color: 'var(--acc)' }}><EnvelopeSimple weight="fill" size={16} /></a>}
                <button type="button" aria-label={`${name} entfernen`} title="Verknüpfung lösen" onClick={() => setEntfernen(e)} disabled={laeuft}
                  className="flex items-center justify-center cursor-pointer" style={{ width: 36, height: 36, borderRadius: 12, border: 'none', background: 'var(--fill2)', color: 'var(--ink2)' }}>
                  <Trash size={16} />
                </button>
              </div>
            </div>
          );
        })}
        <SearchSelect label="Eigentümer hinzufügen" platzhalter="Name, Firma oder E-Mail …" suche={sucheEigentuemer} onWahl={hinzufuegen} />
      </InnenKarte>

      {(verwaltungsart === 'WEG' || verwaltungsart === 'SEV' || wegZeilen.length > 0) && (
        <InnenKarte padding="12px 18px" gap={0}>
          <div style={{ padding: '6px 0 10px' }}><KartenTitel rechts="MEA · Hausgeld-Vorschuss">Eigentümer je Einheit</KartenTitel></div>
          {wegZeilen.length === 0 && <span style={{ padding: '10px 0', fontSize: 13, color: 'var(--ink3)' }}>Noch keine Einheiten-Eigentümer erfasst (in der Einheit unter „Eigentümer“).</span>}
          {wegZeilen.map(w => (
            <div key={w.einheit_id} className="grid items-center" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.6fr) 80px 110px', gap: 10, padding: '10px 0', borderTop: '1px solid var(--line)', fontSize: 13 }}>
              <b className="truncate" style={{ fontWeight: 700 }}>{bez.get(w.einheit_id) ?? '–'}</b>
              {w.eigentuemer_id ? (
                <button type="button" onClick={() => openKontakt(w.eigentuemer_id as string, 'eigentuemer')} className="truncate text-left cursor-pointer" style={{ border: 'none', background: 'transparent', padding: 0, color: 'var(--acc)', fontWeight: 600 }}>{w.name || '–'}</button>
              ) : <span style={{ color: 'var(--ink3)' }}>–</span>}
              <span className="immo-zahl" style={{ textAlign: 'right', color: 'var(--ink2)' }}>{w.mea ?? '–'}</span>
              <span className="immo-zahl" style={{ textAlign: 'right', fontWeight: 700 }}>{w.hausgeld_vorschuss !== null ? eur(w.hausgeld_vorschuss) : '–'}</span>
            </div>
          ))}
        </InnenKarte>
      )}

      {entfernen && (
        <BestaetigenDialog
          titel="Eigentümer lösen?"
          text="Die Verknüpfung zum Objekt wird entfernt, der Eigentümer selbst bleibt erhalten."
          detail={eigentuemerAnzeigename(entfernen)}
          bestaetigenLabel="Lösen"
          laeuft={laeuft}
          onBestaetigen={() => { const name = eigentuemerAnzeigename(entfernen); void setzeListe(ids.filter(i => i !== entfernen.id), `${name} gelöst`).then(() => setEntfernen(null)); }}
          onAbbrechen={() => setEntfernen(null)}
        />
      )}
    </>
  );
}
